// ---------------------------------------------------------------------------
// Rendering what a record owes the printers, so it can be photographed.
//
// The documents are mounted OFF-SCREEN rather than hidden. A `display: none`
// subtree does not lay out and its images never decode, so a receipt captured
// from one arrives with no logo and every line in the wrong place. Pushed to
// the side, it lays out exactly as it would on screen - which is the point:
// what the printer gets is what the preview showed.
//
// The SAME components as the preview and the browser print. There is no second
// renderer and no server-side template to drift out of step - just
// `ReceiptSheet` and `KitchenTicketSheet`, fed the record and the design
// VERSIONS it was written under.
//
// TWO SHAPES OF PLAN come through here, and the difference matters:
//
//   a completed SALE owes a cashier receipt, and a preparation ticket too when
//   something on it needed cutting;
//
//   a DISPATCHED delivery ORDER owes the customer receipt the driver carries
//   and the preparation tickets, with no sale in existence yet. The receipt is
//   rendered from the order's own frozen snapshot (`dispatchReceiptSale`),
//   which is why an order plan must still be given a `sale` - it claims
//   nothing about payment, printing no payment rows at all rather than a fake
//   cash line or an "unpaid" stamp.
//
// ONE SHEET PER BENCH. A sale can owe several preparation tickets - one for
// each bench its lines route to - and each is a different list of work for a
// different person. This used to render `tickets[0]` into a single element and
// hand that same element to every preparation job, so a shop with a grill
// bench and a meat bench sent the grill's list to both.
//
// It also carries the status the cashier reads, because the two belong
// together - the panel is the only visible half of this.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import { ReceiptSheet } from '../print/ReceiptSheet';
import { KitchenTicketSheet } from '../print/KitchenTicketSheet';
import {
  useKitchenConfigForVersion,
  usePrintConfig,
  useReceiptConfigForVersion,
} from '../../api/designs.api';
import { useKitchenTickets } from '../../api/kitchenTickets.api';
import {
  useSalePrinting,
  type PlannedDocument,
  type PrintPlan,
} from '../../hooks/useSalePrinting';
import { toast } from '../../stores/toastStore';
import { FALLBACK_KITCHEN_CONFIG, FALLBACK_RECEIPT_CONFIG } from '../../types/design';
import type { KitchenTicket, Sale } from '../../types';

/// Off the side of the screen, still laid out and still decoding images.
const OFF_SCREEN: CSSProperties = {
  position: 'fixed',
  insetInlineStart: '-200vw',
  top: 0,
  background: '#ffffff',
};

/// ONE bench's ticket, rendered under the design version stamped on it.
///
/// A component of its own rather than a loop in the parent, because each
/// ticket has to look up its OWN design version - and a hook cannot be called
/// once per item of a list. It reports its element and its roll width upwards
/// as soon as both are known.
function BenchTicketSheet({
  document,
  ticket,
  fallbackConfig,
  onSettledLayout,
  registerElement,
}: {
  document: PlannedDocument;
  ticket: KitchenTicket;
  /// The active design, used only where a record was made before the designers
  /// existed and carries no version of its own.
  fallbackConfig: typeof FALLBACK_KITCHEN_CONFIG;
  onSettledLayout: (kitchenTicketId: string, widthMm: 58 | 80, ready: boolean) => void;
  registerElement: (kitchenTicketId: string, element: HTMLDivElement | null) => void;
}) {
  const stamped = useKitchenConfigForVersion(document.designVersionId);
  const config = stamped.data?.kitchen ?? fallbackConfig;
  const widthMm: 58 | 80 = config.widthMm === 58 ? 58 : 80;

  // `isFetched` rather than `data`: a version that no longer exists has to
  // settle into the fallback instead of holding the print open for ever.
  const ready = !document.designVersionId || stamped.isFetched;
  const id = document.kitchenTicketId ?? '';

  useEffect(() => {
    onSettledLayout(id, widthMm, ready);
  }, [id, widthMm, ready, onSettledLayout]);

  return (
    <div ref={(element) => registerElement(id, element)}>
      <KitchenTicketSheet config={config} ticket={ticket} forPreview />
    </div>
  );
}

export interface PrintShopDetails {
  nameEn: string;
  nameAr: string;
  address: string;
  phone: string;
}

export function NetworkPrintSheets({
  plan,
  sale = null,
  shop,
  onSettled,
}: {
  plan: PrintPlan;
  /// Present for a completed sale, absent for an unpaid order.
  sale?: Sale | null;
  /// Only a receipt needs the shop's own details, so an order-only plan may
  /// leave this out.
  shop?: PrintShopDetails;
  /// Called once every document has reached a final state, so whoever owns
  /// the plan can drop it. Without this the last sale's sheets and the last
  /// sale's print states stay mounted underneath the next customer's order.
  onSettled?: () => void;
}) {
  const { t } = useI18n();
  const cashierRef = useRef<HTMLDivElement>(null);
  /// One wrapper per bench, by ticket id.
  const benchRefs = useRef(new Map<string, HTMLDivElement>());
  /// What each bench's stamped design says its roll is, and whether it has
  /// settled enough to be photographed.
  const benchLayout = useRef(new Map<string, { widthMm: 58 | 80; ready: boolean }>());

  const { states, printForSale } = useSalePrinting({
    // The SHEET, not the wrapper. Capturing the wrapper would photograph the
    // white box around the receipt as part of the receipt.
    cashier: () => cashierRef.current?.querySelector<HTMLElement>('.receipt-sheet') ?? null,
    kitchen: (ticketId) =>
      benchRefs.current.get(ticketId)?.querySelector<HTMLElement>('.ticket-sheet') ?? null,
  });

  const cashierDocument = plan.documents.find((d) => d.printerRole === 'CASHIER_RECEIPT');
  // EVERY preparation document, not the first. `find` was the bug: a sale that
  // touched two benches owes two, and the second was never rendered.
  const kitchenDocuments = useMemo(
    () => plan.documents.filter((d) => d.printerRole === 'KITCHEN_TICKET'),
    [plan.documents],
  );

  // The layouts this record was written under, not today's. Both fall back to
  // the active design only when nothing was stamped at all, which is true only
  // of records made before the designers existed.
  const stampedReceipt = useReceiptConfigForVersion(cashierDocument?.designVersionId);
  const { data: active } = usePrintConfig();

  const { data: tickets } = useKitchenTickets(
    plan.saleId ? { saleId: plan.saleId } : { orderId: plan.orderId ?? undefined },
    kitchenDocuments.length > 0,
  );

  /// The ticket each document is a photograph of, matched BY ID. Taking
  /// `tickets[0]` for every document is what made two benches print the same
  /// list of work.
  const benches = useMemo(
    () =>
      kitchenDocuments
        .map((document) => ({
          document,
          ticket: tickets?.find((row) => row.id === document.kitchenTicketId) ?? null,
        }))
        .filter((pair): pair is { document: PlannedDocument; ticket: KitchenTicket } =>
          Boolean(pair.ticket),
        ),
    [kitchenDocuments, tickets],
  );

  const receiptConfig = stampedReceipt.data?.receipt ?? active?.receipt ?? FALLBACK_RECEIPT_CONFIG;
  const kitchenFallback = active?.kitchen ?? FALLBACK_KITCHEN_CONFIG;

  // Everything a document needs has to be here before it can be photographed:
  // a receipt captured mid-load prints half a receipt. `isFetched` rather than
  // `data`, so a missing historical version settles instead of hanging the
  // print for ever - the fallback above then supplies a layout.
  const receiptReady =
    !cashierDocument || !sale || !shop
      ? true
      : cashierDocument.designVersionId
        ? stampedReceipt.isFetched
        : Boolean(active);

  /// Which benches have settled, as state: a ref alone would not re-render,
  /// and the print would wait for a frame that never came.
  const [layoutSettled, setLayoutSettled] = useState<Record<string, boolean>>({});

  const registerElement = useCallback((ticketId: string, element: HTMLDivElement | null) => {
    if (element) benchRefs.current.set(ticketId, element);
    else benchRefs.current.delete(ticketId);
  }, []);

  const onSettledLayout = useCallback(
    (ticketId: string, widthMm: 58 | 80, ready: boolean) => {
      benchLayout.current.set(ticketId, { widthMm, ready });
      setLayoutSettled((current) =>
        current[ticketId] === ready ? current : { ...current, [ticketId]: ready },
      );
    },
    [],
  );

  // EVERY bench has to be ready, not just one. Printing as soon as the first
  // settled would photograph a sheet the second bench had not laid out yet.
  const benchesReady =
    benches.length === kitchenDocuments.length &&
    benches.every(({ document }) => layoutSettled[document.kitchenTicketId ?? ''] === true);

  useEffect(() => {
    if (!receiptReady || !benchesReady) return;

    // One frame, so the sheets are laid out before they are captured.
    const frame = requestAnimationFrame(() => {
      void printForSale(plan, {
        cashier: receiptConfig.widthMm === 58 ? 58 : 80,
        // Each bench's own roll, read from the design its ticket was stamped
        // with. Falls back to 80 only if a bench somehow never reported.
        kitchen: (ticketId) => benchLayout.current.get(ticketId)?.widthMm ?? 80,
      });
    });

    return () => cancelAnimationFrame(frame);
    // `printForSale` refuses to run twice for the same record, so a re-render
    // here costs nothing and cannot produce a second receipt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptReady, benchesReady, plan.saleId, plan.orderId]);

  /// Documents already spoken about, so a re-render cannot say it twice.
  const announced = useRef(new Set<string>());

  // One short toast per document, the moment it settles. Not a dialogue and
  // not a preview: the cashier is already serving the next customer, and the
  // only thing worth interrupting them for is a document that did NOT go.
  useEffect(() => {
    for (const state of states) {
      if (state.status === 'PREPARING' || state.status === 'PRINTING') continue;

      // Keyed by the DOCUMENT, so two benches each get their own word. Keyed
      // by role, the meat bench's failure was swallowed as a repeat of the
      // grill's success.
      const key = `${plan.saleId ?? plan.orderId ?? ''}:${state.key}:${state.status}`;
      if (announced.current.has(key)) continue;
      announced.current.add(key);

      const kitchen = state.role === 'KITCHEN_TICKET';

      // WHICH BENCH, whenever the plan named one. With three benches, "the
      // preparation ticket was not sent" leaves the cashier looking at three
      // printers; naming it is the difference between a message and an
      // instruction. The unnamed wording stays for the older jobs that carry
      // no printer.
      const named = (withName: string, plain: string) =>
        state.printerName ? withName.replace('{name}', state.printerName) : plain;

      if (state.status === 'SENT') {
        toast.success(
          kitchen
            ? named(t.salePrint.kitchenSentAt, t.salePrint.kitchenSent)
            : t.salePrint.cashierSent,
        );
      } else if (state.status === 'QUEUED') {
        // QUEUED FOR NOBODY. When the shop's print agent is not running, every
        // document waits for ever and "queued for retry" is a lie that leaves
        // the cashier standing at a printer. Said once per document, with the
        // reassurance that nothing was lost - because nothing was.
        if (state.agentSilent) {
          toast.error(t.salePrint.agentSilentToast, t.salePrint.agentSilentDetail);
        } else {
          toast.info(
            kitchen
              ? named(t.salePrint.kitchenQueuedAt, t.salePrint.kitchenQueued)
              : t.salePrint.cashierQueued,
          );
        }
      } else if (state.status === 'FAILED') {
        // NOT called uncertain. A job that failed before its socket was
        // written to printed nothing; uncertainty belongs to a send that was
        // interrupted part-way, which the local print history records as
        // `uncertain` and which nothing ever reprints on its own.
        toast.error(
          kitchen
            ? named(t.salePrint.kitchenFailedAt, t.salePrint.kitchenFailedToast)
            : t.salePrint.cashierFailedToast,
        );
      }
    }
  }, [states, plan.saleId, plan.orderId, t]);

  // Once every document has finished, the plan has nothing left to do and the
  // caller is told so it can drop it. This is what stops the LAST sale's print
  // state - and its off-screen sheets - from still being mounted while the
  // cashier builds the next customer's order.
  const settled = useRef(false);
  useEffect(() => {
    if (settled.current) return;
    if (states.length !== plan.documents.length) return;
    if (states.some((s) => s.status === 'PREPARING' || s.status === 'PRINTING')) return;

    settled.current = true;
    onSettled?.();
  }, [states, plan.documents.length, onSettled]);

  return (
    <>
      {/* NOTHING VISIBLE LIVES HERE ANY MORE.
          There used to be a second status surface pinned to the other bottom
          corner of the till, saying in a green box what the toast had just
          said in words. Two surfaces for one fact is one surface too many:
          the cashier read the corner nearest them, and on the shop screen the
          box sat over the payment controls. The toasts above are the only
          printing feedback now, and a toast floats - it cannot move the till
          underneath it. */}

      {/* The documents themselves, off to the side. */}
      <div aria-hidden="true" style={OFF_SCREEN}>
        {cashierDocument && sale && shop ? (
          <div ref={cashierRef}>
            <ReceiptSheet config={receiptConfig} sale={sale} shop={shop} forPreview />
          </div>
        ) : null}

        {/* ONE PER BENCH. Every preparation document gets its own sheet, its
            own design version and its own element to be photographed from. */}
        {benches.map(({ document, ticket }) => (
          <BenchTicketSheet
            key={document.kitchenTicketId}
            document={document}
            ticket={ticket}
            fallbackConfig={kitchenFallback}
            onSettledLayout={onSettledLayout}
            registerElement={registerElement}
          />
        ))}
      </div>
    </>
  );
}
